import type {Component, Editor} from 'grapesjs';
import {initMergeTagsDropdown, renderAiRewritePromptBoxDropdown, TOOLBAR_MERGE_TAGS_LIST_ID} from '@Global/email-maker';
import {appendMergeTagToComponent} from '@EmailMakerReact/lib/email-maker-merge-tag-library';
import {hydrateMergeTagsReduxFromComponent, updateMergeTags} from '@EmailMakerReact/lib/email-maker-social-signup-library';
import {isAiEditEnabled} from '@EmailMakerReact/lib/email-maker-ai-library';
import {TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';

const MERGE_TAG_ANCHOR_CLASS = 'merge-tag-anchor';

export interface ToolbarDropdownObservers {
  componentMutationObserver: MutationObserver;
  /** Null when the toolbar element wasn't available at mount time (nothing to observe). */
  mergeTagsToolbarObserver: MutationObserver | null;
  aiRewriteToolbarObserver: MutationObserver | null;
}

/**
 * Mounts the merge-tag and AI-rewrite dropdowns into the GrapesJS toolbar for a selected TEXT or
 * BUTTON component, returning the observers so the caller can disconnect them later.
 */
export function mountToolbarDropdowns(editor: Editor, component: Component): ToolbarDropdownObservers | null {
  const toolbarEl = editor.Canvas.getToolbarEl();
  const componentEl = component.getEl();

  if (!componentEl) {
    return null;
  }

  hydrateMergeTagsReduxFromComponent(component);

  const componentMutationObserver = window.PMW.attachAndGetMutationObserverToElement(
    componentEl,
    (mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          updateMergeTags(editor, component);
        }
      });
    },
    {attributes: true, childList: true, characterData: true, subtree: true}
  );

  if (!toolbarEl) {
    return {
      componentMutationObserver,
      mergeTagsToolbarObserver: null,
      aiRewriteToolbarObserver: null,
    };
  }

  const mergeTagsToolbarObserver = window.PMW.attachAndGetMutationObserverToElement(
    toolbarEl,
    () => {
      const anchorEl = toolbarEl.getElementsByClassName(MERGE_TAG_ANCHOR_CLASS)[0];

      if (!anchorEl || anchorEl.hasChildNodes()) {
        return;
      }

      initMergeTagsDropdown(anchorEl, {
        listId: TOOLBAR_MERGE_TAGS_LIST_ID,
        onSelect: (value) => {
          const currSelectedComponent = editor.getSelected();

          if (!currSelectedComponent) {
            return;
          }

          appendMergeTagToComponent(currSelectedComponent, value, TOOLBAR_MERGE_TAGS_LIST_ID);
        },
      });
    },
    {childList: true, subtree: true}
  );

  const aiRewriteToolbarObserver = window.PMW.attachAndGetMutationObserverToElement(
    toolbarEl,
    () => {
      const anchorEl = document.getElementById(TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR);

      if (!anchorEl || anchorEl.hasChildNodes()) {
        return;
      }

      renderAiRewritePromptBoxDropdown(anchorEl, {
        editor,
        showPromptBoxOnRender: isAiEditEnabled(editor),
      });
    },
    {childList: true, subtree: true}
  );

  return {componentMutationObserver, mergeTagsToolbarObserver, aiRewriteToolbarObserver};
}

export function disconnectToolbarDropdownObservers(observers: ToolbarDropdownObservers | null): void {
  if (!observers) {
    return;
  }

  observers.componentMutationObserver.disconnect();
  observers.mergeTagsToolbarObserver?.disconnect();
  observers.aiRewriteToolbarObserver?.disconnect();
}

export function isToolbarDropdownComponent(component: Component): boolean {
  return component.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT) || component.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON);
}
